import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { moodboardService } from '../lib/moodboardService';
import { RoomCategory, RoomStyle, RoomSize } from '../types/moodboard';

interface MoodboardContextType {
  categories: RoomCategory[];
  styles: RoomStyle[];
  sizes: RoomSize[];
  loading: boolean;
  preloadMoodboardData: () => Promise<void>;
}

const MoodboardContext = createContext<MoodboardContextType | undefined>(undefined);

export const useMoodboard = () => {
  const context = useContext(MoodboardContext);
  if (!context) {
    throw new Error('useMoodboard must be used within a MoodboardProvider');
  }
  return context;
};

interface MoodboardProviderProps {
  children: ReactNode;
}

export const MoodboardProvider: React.FC<MoodboardProviderProps> = ({ children }) => {
  const [categories, setCategories] = useState<RoomCategory[]>([]);
  const [styles, setStyles] = useState<RoomStyle[]>([]);
  const [sizes, setSizes] = useState<RoomSize[]>([]);
  const [loading, setLoading] = useState(false);
  const [isPreloaded, setIsPreloaded] = useState(false);

  const preloadMoodboardData = useCallback(async () => {
    // Prevent re-fetching if data is already loaded or currently loading
    if (isPreloaded || loading) {
      console.log('Moodboard data already preloaded or loading, skipping...');
      return;
    }
    
    console.log('Preloading moodboard data...');
    setLoading(true);
    try {
      const [categoriesData, stylesData, sizesData] = await Promise.all([
        moodboardService.getRoomCategories(),
        moodboardService.getRoomStyles(),
        moodboardService.getRoomSizes(),
      ]);
      setCategories(categoriesData);
      setStyles(stylesData);
      setSizes(sizesData);
      setIsPreloaded(true);
      console.log('Moodboard data preloaded successfully.');
    } catch (error) {
      console.error('Failed to preload moodboard data', error);
      // We can decide if we want to expose this error state
    } finally {
      setLoading(false);
    }
  }, [isPreloaded, loading]);

  const value = {
    categories,
    styles,
    sizes,
    loading,
    preloadMoodboardData,
  };

  return (
    <MoodboardContext.Provider value={value}>
      {children}
    </MoodboardContext.Provider>
  );
}; 